/**
 * Promo bar countdown. Two modes:
 *
 *   deadline  — counts to a fixed date the merchant sets. Honest: it ends.
 *   evergreen — a per-visitor window that restarts once it lapses. The urgency
 *               is manufactured; the deadline is not real. Chosen deliberately
 *               here, matching the pattern both competitors run.
 *
 * The evergreen window is stored per visitor so it does not restart on every
 * page view within a session — a timer that resets on navigation is obvious.
 */
const STORE_KEY = 'ms-countdown-ends-at';

class MsCountdown extends HTMLElement {
  connectedCallback() {
    this.timerEl = this.querySelector('[data-ms-countdown-timer]');
    this.expiredEl = this.querySelector('[data-ms-countdown-expired]');
    this.hours = this.querySelector('[data-ms-countdown-hours]');
    this.minutes = this.querySelector('[data-ms-countdown-minutes]');
    this.seconds = this.querySelector('[data-ms-countdown-seconds]');

    this.evergreen = this.dataset.mode === 'evergreen';
    this.endsAt = this.evergreen ? this.evergreenDeadline() : this.fixedDeadline();
    if (this.endsAt === null) return;

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  fixedDeadline() {
    const raw = (this.dataset.end || '').trim();
    if (!raw) return null; // No deadline set: leave the static message alone.
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) {
      console.warn('[ms-countdown] Unparseable end date:', raw);
      return null;
    }
    return t;
  }

  evergreenDeadline() {
    const windowMs = (parseInt(this.dataset.windowMinutes, 10) || 120) * 60000;

    // localStorage can throw in private mode; fall back to a fresh window.
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORE_KEY);
    } catch (e) {
      return Date.now() + windowMs;
    }

    const parsed = stored ? parseInt(stored, 10) : NaN;
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;

    const next = Date.now() + windowMs;
    try {
      window.localStorage.setItem(STORE_KEY, String(next));
    } catch (e) {
      /* not persistable; the window simply restarts next load */
    }
    return next;
  }

  tick() {
    const remaining = this.endsAt - Date.now();

    if (remaining <= 0) {
      if (this.evergreen) {
        // Window lapsed while the page sat open. Start the next one rather
        // than hiding the bar.
        this.endsAt = this.evergreenDeadline();
        return;
      }
      clearInterval(this.interval);
      this.timerEl?.setAttribute('hidden', '');

      if (this.dataset.expiredBehavior === 'message' && this.expiredEl?.textContent.trim()) {
        this.expiredEl.removeAttribute('hidden');
      } else {
        this.setAttribute('hidden', '');
      }
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    // Hours accumulate past 24 rather than rolling into a days field, matching
    // how short-window promo bars in this category display.
    if (this.hours) this.hours.textContent = pad(Math.floor(totalSeconds / 3600));
    if (this.minutes) this.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
    if (this.seconds) this.seconds.textContent = pad(totalSeconds % 60);

    this.timerEl?.removeAttribute('hidden');
  }
}

if (!customElements.get('ms-countdown')) {
  customElements.define('ms-countdown', MsCountdown);
}
